import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowLeft, Calendar, MapPin, Users, Send, Star, Check, X, UserX, Vote, Clock } from 'lucide-react';
import { toast } from 'sonner';
import RatingModal from '../components/RatingModal';
import GroupRideRatingModal from '../components/GroupRideRatingModal';

export default function RideDetail({ user }) {
  const { rideId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [ride, setRide] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [routeVotes, setRouteVotes] = useState(null);
  const [userVote, setUserVote] = useState(null);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const isHost = ride?.creator_id === user?.id;
  const isGroupRide = ride?.ride_type === 'group';

  // Check if we should show rating modal from URL param
  useEffect(() => {
    if (searchParams.get('showRating') === 'true' && ride?.status === 'completed') {
      setShowRatingModal(true);
    }
  }, [searchParams, ride]);

  useEffect(() => {
    fetchRideDetails();
    fetchParticipants();
    fetchMessages();

    // Poll for new messages every 3 seconds
    pollIntervalRef.current = setInterval(() => {
      fetchMessages();
      if (isHost) {
        fetchPendingRequests();
      }
    }, 3000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [rideId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isGroupRide) {
      fetchRouteVotes();
    }
    if (isHost) {
      fetchPendingRequests();
      fetchWaitlist();
    }
  }, [ride]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchRideDetails = async () => {
    try {
      console.log('[RideDetail] Fetching ride details for:', rideId);
      console.log('[RideDetail] API baseURL:', axios.defaults.baseURL);
      console.log('[RideDetail] Auth header:', axios.defaults.headers.common['Authorization'] ? 'Present' : 'MISSING');
      
      const response = await axios.get(`/rides/${rideId}`);
      console.log('[RideDetail] SUCCESS - Ride details received:', response.data);
      setRide(response.data);
    } catch (error) {
      // VERBOSE PRODUCTION ERROR LOGGING - DO NOT REMOVE
      console.error('============================================');
      console.error('[RideDetail] CRITICAL ERROR - fetchRideDetails FAILED');
      console.error('[RideDetail] Ride ID:', rideId);
      console.error('[RideDetail] Error name:', error.name);
      console.error('[RideDetail] Error message:', error.message);
      console.error('[RideDetail] Error code:', error.code);
      console.error('[RideDetail] Response status:', error.response?.status);
      console.error('[RideDetail] Response data:', JSON.stringify(error.response?.data));
      console.error('[RideDetail] Request URL:', error.config?.url);
      console.error('[RideDetail] Request baseURL:', error.config?.baseURL);
      console.error('[RideDetail] Full error object:', error);
      console.error('============================================');
      
      if (error.response?.status === 404) {
        toast.error('Ride not found - it may have been deleted');
      } else if (error.response?.status === 401) {
        toast.error('Session expired - please log in again');
      } else if (error.response?.status === 403) {
        toast.error('You do not have access to this ride');
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Network error - please check your connection');
      } else {
        toast.error(`Failed to load ride: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchParticipants = async () => {
    try {
      console.log('Fetching participants for ride:', rideId);
      const response = await axios.get(`/rides/${rideId}/participants`);
      console.log('Participants received:', response.data);
      setParticipants(response.data);
    } catch (error) {
      console.error('Failed to fetch participants:', error.response?.status, error.response?.data);
    }
  };

  const fetchPendingRequests = async () => {
    try {
      const response = await axios.get(`/rides/${rideId}/pending-requests`);
      setPendingRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch pending requests');
    }
  };

  const fetchWaitlist = async () => {
    try {
      const response = await axios.get(`/rides/${rideId}/waitlist`);
      setWaitlist(response.data);
    } catch (error) {
      console.error('Failed to fetch waitlist');
    }
  };

  const fetchRouteVotes = async () => {
    try {
      const response = await axios.get(`/rides/${rideId}/route-votes`);
      setRouteVotes(response.data);
    } catch (error) {
      console.error('Failed to fetch route votes');
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`/rides/${rideId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      await axios.post(`/rides/${rideId}/messages`, { content: newMessage });
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleApproveRider = async (userId) => {
    try {
      await axios.post(`/rides/${rideId}/participants/${userId}/approve`);
      toast.success('Rider approved!');
      fetchPendingRequests();
      fetchParticipants();
    } catch (error) {
      toast.error('Failed to approve rider');
    }
  };

  const handleDeclineRider = async (userId) => {
    try {
      await axios.post(`/rides/${rideId}/participants/${userId}/decline`);
      toast.success('Rider declined');
      fetchPendingRequests();
    } catch (error) {
      toast.error('Failed to decline rider');
    }
  };

  const handleMarkNoShow = async (userId) => {
    if (!confirm('Mark this rider as a no-show? This will affect their reputation.')) return;
    try {
      await axios.post(`/rides/${rideId}/participants/${userId}/no-show`);
      toast.success('Rider marked as no-show');
      fetchParticipants();
    } catch (error) {
      toast.error('Failed to mark no-show');
    }
  };

  const handleVoteRoute = async (routeIndex) => {
    try {
      await axios.post(`/rides/${rideId}/vote-route?route_index=${routeIndex}`);
      setUserVote(routeIndex);
      toast.success('Vote recorded!');
      fetchRouteVotes();
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  const handlePromoteFromWaitlist = async () => {
    try {
      await axios.post(`/rides/${rideId}/promote-from-waitlist`);
      toast.success('Promoted rider from waitlist!');
      fetchWaitlist();
      fetchParticipants();
      fetchPendingRequests();
    } catch (error) {
      toast.error('Failed to promote from waitlist');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400">Loading ride details...</p>
      </div>
    );
  }

  if (!ride) return null;

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-xl font-bold text-white">
            {isGroupRide ? '👥 Group Ride' : 'Ride Details'}
          </h1>
          {isGroupRide && (
            <Badge className="bg-purple-600">Group Ride</Badge>
          )}
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Ride Info & Participants */}
          <div className="lg:col-span-1 space-y-6">
            {/* Ride Info Card */}
            <Card data-testid="ride-detail-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle data-testid="ride-detail-distance">{ride.distance}km Ride</CardTitle>
                  <Badge variant={ride.status === 'completed' ? 'default' : 'secondary'} data-testid="ride-detail-status">
                    {ride.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-purple-500" />
                  <span className="text-muted-foreground" data-testid="ride-detail-datetime">{ride.date} at {ride.time}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-purple-500" />
                  <div className="flex-1">
                    <span className="text-muted-foreground" data-testid="ride-detail-location">{ride.postcode || ride.location}</span>
                    {ride.location && ride.postcode && (
                      <p className="text-muted-foreground text-xs mt-1">{ride.location}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4 text-purple-500" />
                  <span className="text-muted-foreground">
                    {participants.length}{ride.max_riders ? ` / ${ride.max_riders}` : ''} rider{participants.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Route voting for group rides - only shown to participants, not the host */}
                {isGroupRide && ride.route_options && ride.route_options.length > 0 && (
                  <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                    <p className="text-purple-500 font-semibold text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Vote className="w-4 h-4" /> {isHost ? 'Route Options' : 'Vote for Route'}
                    </p>
                    {isHost && (
                      <p className="text-xs text-muted-foreground mb-3">As the host, you cannot vote. Riders will choose the route.</p>
                    )}
                    <div className="space-y-2">
                      {ride.route_options.map((route, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-800/50 rounded">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-foreground">{route.name}</p>
                            <a href={route.link} target="_blank" rel="noopener noreferrer" className="text-purple-500 text-xs hover:underline">
                              View map →
                            </a>
                          </div>
                          <div className="flex items-center gap-2">
                            {routeVotes && (
                              <span className="text-xs text-muted-foreground">
                                {routeVotes.votes[String(idx)] || 0} votes
                              </span>
                            )}
                            {!isHost && (
                              <Button
                                size="sm"
                                variant={userVote === idx ? 'default' : 'outline'}
                                onClick={() => handleVoteRoute(idx)}
                                disabled={ride.status === 'completed'}
                              >
                                {userVote === idx ? '✓' : 'Vote'}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Single route link for individual rides */}
                {!isGroupRide && ride.route_link && (
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-purple-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    <div>
                      <p className="text-foreground font-medium">Route Map</p>
                      <a
                        href={ride.route_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-500 text-sm hover:underline break-all"
                        data-testid="ride-detail-route-link"
                      >
                        View on GPS device
                      </a>
                    </div>
                  </div>
                )}

                {ride.route_description && (
                  <div>
                    <p className="text-foreground font-medium mb-2">Route</p>
                    <p className="text-muted-foreground text-sm" data-testid="ride-detail-route">{ride.route_description}</p>
                  </div>
                )}

                {/* Rating button */}
                {ride.status === 'completed' && (
                  <Button
                    onClick={() => setShowRatingModal(true)}
                    className="w-full mt-4"
                    data-testid="rate-riders-button"
                  >
                    <Star className="w-4 h-4 mr-2" />
                    {isGroupRide ? 'Rate Host & Route' : 'Rate Riders'}
                  </Button>
                )}

                {/* Complete ride button for host */}
                {isHost && ride.status === 'active' && (
                  <Button
                    onClick={async () => {
                      try {
                        await axios.post(`/rides/${rideId}/complete`);
                        toast.success('Ride marked as completed!');
                        fetchRideDetails();
                      } catch (error) {
                        toast.error('Failed to complete ride');
                      }
                    }}
                    variant="outline"
                    className="w-full"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Mark Ride Complete
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Pending Requests (Host Only) */}
            {isHost && pendingRequests.length > 0 && (
              <Card className="border-yellow-500/30">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-yellow-500" />
                    Pending Requests ({pendingRequests.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pendingRequests.map((rider) => (
                      <div key={rider.id} className="flex items-center gap-3 p-2 bg-yellow-500/10 rounded-lg">
                        <div className="w-10 h-10 bg-yellow-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold">
                            {rider.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="text-foreground font-medium">{rider.name}</p>
                          <div className="flex items-center gap-1">
                            <Star className="w-3 h-3 text-yellow-500" fill="currentColor" />
                            <span className="text-xs text-muted-foreground">
                              {rider.avg_rating.toFixed(1)}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="border-green-500 text-green-500 hover:bg-green-500/20" onClick={() => handleApproveRider(rider.id)}>
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="border-red-500 text-red-500 hover:bg-red-500/20" onClick={() => handleDeclineRider(rider.id)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Waitlist (Host Only) */}
            {isHost && waitlist.length > 0 && (
              <Card className="border-blue-500/30">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    Waitlist ({waitlist.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-3">
                    {waitlist.map((rider, idx) => (
                      <div key={rider.id} className="flex items-center gap-3 text-sm">
                        <span className="text-muted-foreground">#{idx + 1}</span>
                        <span className="text-foreground">{rider.name}</span>
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-yellow-500" fill="currentColor" />
                          <span className="text-xs text-muted-foreground">{rider.avg_rating.toFixed(1)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button size="sm" variant="outline" onClick={handlePromoteFromWaitlist} className="w-full">
                    Promote Next Rider
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Participants Card */}
            <Card data-testid="participants-card">
              <CardHeader>
                <CardTitle className="text-lg">
                  {isGroupRide ? 'Group Members' : 'Riders'} ({participants.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {participants.map((participant) => (
                    <div key={participant.id} className="flex items-center gap-3" data-testid="participant-item">
                      <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold">
                          {participant.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="text-foreground font-medium" data-testid="participant-name">
                          {participant.name}
                          {participant.id === ride.creator_id && (
                            <Badge variant="outline" className="ml-2 text-xs">Host</Badge>
                          )}
                        </p>
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-yellow-500" fill="currentColor" />
                          <span className="text-xs text-muted-foreground" data-testid="participant-rating">
                            {participant.avg_rating.toFixed(1)}
                          </span>
                          <span className="text-muted-foreground mx-1">•</span>
                          <span className="text-xs text-muted-foreground">{participant.experience_level}</span>
                        </div>
                      </div>
                      {/* No-show button for host (only after ride is completed) */}
                      {isHost && ride.status === 'completed' && participant.id !== user.id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          onClick={() => handleMarkNoShow(participant.id)}
                          title="Mark as no-show"
                        >
                          <UserX className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Chat */}
          <Card className="lg:col-span-2" data-testid="chat-card">
            <CardHeader>
              <CardTitle>
                {isGroupRide ? '👥 Group Chat' : 'Chat'}
                {isGroupRide && (
                  <p className="text-xs text-muted-foreground font-normal mt-1">
                    Messages are shared with all group members
                  </p>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Separator />
              <ScrollArea className="h-[500px] p-4">
                <div className="space-y-4">
                  {messages.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No messages yet. Start the conversation!</p>
                  ) : (
                    messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex gap-3 ${message.user_id === user.id ? 'flex-row-reverse' : ''}`}
                        data-testid="chat-message"
                      >
                        <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs font-bold">
                            {message.user?.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div
                          className={`flex-1 max-w-md ${
                            message.user_id === user.id ? 'text-right' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-foreground" data-testid="message-sender">
                              {message.user?.name}
                              {message.user_id === ride?.creator_id && (
                                <Badge variant="outline" className="ml-1 text-xs py-0">Host</Badge>
                              )}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(message.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>
                          <div
                            className={`inline-block px-4 py-2 rounded-lg ${
                              message.user_id === user.id
                                ? 'bg-purple-600 text-white'
                                : 'bg-accent text-foreground'
                            }`}
                            data-testid="message-content"
                          >
                            {message.content}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>
              <Separator />
              <form onSubmit={handleSendMessage} className="p-4">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1"
                    data-testid="message-input"
                  />
                  <Button type="submit" disabled={!newMessage.trim()} data-testid="send-message-button">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Rating Modal - Different for group vs individual rides */}
      {showRatingModal && (
        isGroupRide ? (
          <GroupRideRatingModal
            rideId={rideId}
            ride={ride}
            onClose={() => setShowRatingModal(false)}
            onRated={() => {
              setShowRatingModal(false);
              toast.success('Thanks for rating!');
            }}
          />
        ) : (
          <RatingModal
            rideId={rideId}
            participants={participants.filter((p) => p.id !== user.id)}
            onClose={() => setShowRatingModal(false)}
            onRated={() => {
              setShowRatingModal(false);
              toast.success('Ratings submitted!');
            }}
          />
        )
      )}
    </div>
  );
}
